// externals
import type { PropsWithChildren } from 'react';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { BootstrapLoadingState } from '@makemydeal/dr-shared-types';
import { sharedBootstrapSelectors } from '@makemydeal/dr-shared-store';
import { markInitDispatch, reportAuthGate } from '@makemydeal/dr-shared-components';
import { loadingIndicatorUtils, SALES_CHECKIN_APP_PREFIX } from '@makemydeal/dr-shared-ui-utils';
import { useAppRouter } from '@makemydeal/dr-sales-checkin-ui-utils';

import { initialLoad, fetchBootstrapData } from '../../actions/bootstrapAppActionCreators';

// components
import { BootstrapError } from '../bootstrapError/BootstrapError';
import { LoadingPage } from '../loadingPage/LoadingPage';

export const Bootstrapper: React.FC<PropsWithChildren> = ({ children }) => {
    const dispatch = useDispatch();
    const { location } = useAppRouter();

    const loadingState = useSelector(sharedBootstrapSelectors.getBootstrapLoadingState);
    const loadingErrorMessage = useSelector(sharedBootstrapSelectors.getBootstrapErrorMessage);

    useEffect(() => {
        // load the deal props
        dispatch(initialLoad());
        // fetch the deal
        markInitDispatch();
        dispatch(fetchBootstrapData(SALES_CHECKIN_APP_PREFIX));
        reportAuthGate();
    }, [dispatch]); // even though dispatch is a stable reference, it is added to keep the linter happy

    useEffect(() => {
        if (loadingState !== BootstrapLoadingState.LoadedSuccessfully && loadingState !== BootstrapLoadingState.LoadFailed) return;

        loadingIndicatorUtils.removeAllLoadingIndicatorPanels();
    }, [loadingState]);

    useEffect(() => {
        window.scroll({
            top: 0,
            left: 0
        });
    }, [location]);

    if (loadingErrorMessage) return <BootstrapError loadingError={loadingErrorMessage} />;

    return (
        <>
            {loadingState === BootstrapLoadingState.Loading && <LoadingPage />}
            {loadingState === BootstrapLoadingState.LoadedSuccessfully && children}
            {loadingState === BootstrapLoadingState.LoadFailed && <BootstrapError />}
            {loadingState.toString() === '' && 'Error occurred bootstrapping the app - unsupport state.'}
        </>
    );
};
